import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { ChevronLeft, ChevronRight, Fingerprint, Globe, Loader2, LogOut, Map as MapIcon, Monitor, RefreshCw, Search, Shield, ShieldCheck, Smartphone, Trash2, Upload, X } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { HomeRegionSection } from './HomeRegionSection';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    const coords = points.filter(p => p.lat != null && p.lng != null).map(p => [p.lat, p.lng]);
    if (coords.length === 1) map.setView(coords[0], 6);
    else if (coords.length > 1) map.fitBounds(coords, { padding: [40, 40], maxZoom: 10 });
  }, [points, map]);
  return null;
};
import { toast } from 'sonner';
import { formatDT } from '../../utils/timezone';
import { formatUTCStamp } from '../../utils/dateFormat';
import { exportSigninMap } from '../../utils/signinMapExport';

const PAGE_SIZE = 25;

const SIGNAL_LABELS = { plt: 'Platform', cpu: 'CPU cores', mem: 'Memory (GB)', tch: 'Touch points', lng: 'Language', cnv: 'Canvas ID', scr: 'Screen', tz: 'Timezone' };
const parseSignals = (s) => !s ? [] : s.split('|').map((part) => {
  const i = part.indexOf(':');
  if (i < 1) return null;
  return [SIGNAL_LABELS[part.slice(0, i)] || part.slice(0, i), part.slice(i + 1)];
}).filter(Boolean);

const currentSessionId = () =>
  sessionStorage.getItem('quick_login_session_id') || localStorage.getItem('session_id') || undefined;

const Pager = ({ page, pages, onPrev, onNext, prefix }) => {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 pt-1">
      <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1} onClick={onPrev} data-testid={`${prefix}-pager-prev`}>
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <span className="text-xs text-muted-foreground" data-testid={`${prefix}-pager-label`}>Page {page} of {pages}</span>
      <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= pages} onClick={onNext} data-testid={`${prefix}-pager-next`}>
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );
};

export const DeviceSecurityCard = ({ api }) => {
  const [view, setView] = useState('sessions');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  // ── Active sessions ──
  const [deviceSessions, setDeviceSessions] = useState([]);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  // ── Remembered devices & networks ──
  const [devices, setDevices] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [devicesTotal, setDevicesTotal] = useState(0);
  const [networksTotal, setNetworksTotal] = useState(0);
  const [geoipStatus, setGeoipStatus] = useState(null);
  const [trustedLoading, setTrustedLoading] = useState(false);
  const [expandedNets, setExpandedNets] = useState([]);
  const [confirmingKey, setConfirmingKey] = useState(null);
  const [removingKey, setRemovingKey] = useState(null);
  const [expandedSignals, setExpandedSignals] = useState([]);
  // ── Sign-in map ──
  const [mapPoints, setMapPoints] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapDays, setMapDays] = useState(30);
  const [purging, setPurging] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { setPage(1); }, [debouncedSearch, view]);

  const fetchDeviceSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const { data } = await api.get('/auth/sessions', {
        params: {
          current_session_id: currentSessionId(),
          search: debouncedSearch || undefined,
          skip: (page - 1) * PAGE_SIZE,
          limit: PAGE_SIZE,
        },
      });
      setDeviceSessions(data.sessions || []);
      setSessionsTotal(data.total ?? (data.sessions || []).length);
    } catch (e) { /* list stays as-is */ } finally {
      setSessionsLoading(false);
    }
  }, [api, debouncedSearch, page]);

  const fetchTrusted = useCallback(async () => {
    setTrustedLoading(true);
    try {
      const res = await api.get('/security/trusted-devices', {
        params: { search: debouncedSearch || undefined, skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE },
      });
      setDevices(res.data?.devices || []);
      setNetworks(res.data?.networks || []);
      setDevicesTotal(res.data?.devices_total ?? 0);
      setNetworksTotal(res.data?.networks_total ?? 0);
      setGeoipStatus(res.data?.geoip_status || null);
    } catch {
      setDevices([]);
      setNetworks([]);
    } finally {
      setTrustedLoading(false);
    }
  }, [api, debouncedSearch, page]);

  useEffect(() => { if (view === 'sessions') fetchDeviceSessions(); }, [view, fetchDeviceSessions]);
  useEffect(() => { if (view === 'trusted') fetchTrusted(); }, [view, fetchTrusted]);

  const fetchMap = useCallback(async () => {
    setMapLoading(true);
    try {
      const res = await api.get('/security/login-map', { params: { days: mapDays } });
      setMapPoints(res.data?.points || []);
    } catch {
      setMapPoints([]);
    } finally {
      setMapLoading(false);
    }
  }, [api, mapDays]);
  useEffect(() => { if (view === 'map') fetchMap(); }, [view, fetchMap]);

  const handlePurgeStale = async () => {
    if (confirmingKey !== 'purge') {
      setConfirmingKey('purge');
      setTimeout(() => setConfirmingKey((c) => (c === 'purge' ? null : c)), 4000);
      return;
    }
    setConfirmingKey(null);
    setPurging(true);
    try {
      const { data } = await api.post('/auth/sessions/purge-stale', {
        current_session_id: currentSessionId() || null, days: 30,
      });
      toast.success(`Purged ${data.purged} stale session${data.purged === 1 ? '' : 's'} (30+ days idle)`);
      fetchDeviceSessions();
    } catch (e) {
      toast.error('Could not purge stale sessions');
    } finally {
      setPurging(false);
    }
  };

  const handleRevokeSession = async (sid) => {
    try {
      await api.delete(`/auth/sessions/${sid}`);
      toast.success('Device signed out');
      fetchDeviceSessions();
    } catch (e) {
      toast.error('Could not sign out that device');
    }
  };

  const handleRevokeOtherSessions = async () => {
    try {
      const { data } = await api.post('/auth/sessions/revoke-others', { current_session_id: currentSessionId() || null });
      toast.success(`Signed out ${data.revoked} other session${data.revoked === 1 ? '' : 's'}`);
      fetchDeviceSessions();
    } catch (e) {
      toast.error('Could not sign out other devices');
    }
  };

  const removeTrusted = async (kind, key, signout = false) => {
    const id = `${kind}:${key}${signout ? ':so' : ''}`;
    if (confirmingKey !== id) {
      setConfirmingKey(id);
      setTimeout(() => setConfirmingKey((c) => (c === id ? null : c)), 4000);
      return;
    }
    setConfirmingKey(null);
    setRemovingKey(id);
    try {
      if (kind === 'device') {
        const { data } = await api.delete(`/security/trusted-devices/${encodeURIComponent(key)}${signout ? '?signout=true' : ''}`);
        toast.success(signout
          ? `Device forgotten and ${data.signed_out_sessions || 0} session${data.signed_out_sessions === 1 ? '' : 's'} signed out`
          : 'Device forgotten — its next sign-in will be flagged');
      } else {
        await api.delete(`/security/trusted-networks/${encodeURIComponent(key)}`);
        toast.success('Network forgotten — its next sign-in will be flagged');
      }
      fetchTrusted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove');
    } finally {
      setRemovingKey(null);
    }
  };

  const ConfirmBtn = ({ kind, itemKey, signout = false, label, icon, variant = 'outline', testId }) => {
    const id = `${kind}:${itemKey}${signout ? ':so' : ''}`;
    const confirming = confirmingKey === id;
    return (
      <Button
        variant={confirming ? 'destructive' : variant}
        size="sm"
        className="h-7 shrink-0"
        disabled={removingKey === id}
        onClick={() => removeTrusted(kind, itemKey, signout)}
        title={confirming ? 'Click again to confirm' : label}
        data-testid={testId}
      >
        {removingKey === id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (confirming ? 'Confirm?' : icon)}
      </Button>
    );
  };

  const loading = view === 'sessions' ? sessionsLoading : view === 'map' ? mapLoading : trustedLoading;
  const sessionPages = Math.max(1, Math.ceil(sessionsTotal / PAGE_SIZE));
  const trustedPages = Math.max(1, Math.ceil(Math.max(devicesTotal, networksTotal) / PAGE_SIZE));

  const mapRef = useRef(null);
  const cardRef = useRef(null);
  // Security-alert deep-links land on /settings?tab=general#sign-in-security —
  // bring this card into view since it sits far down the page.
  useEffect(() => {
    if (window.location.hash === '#sign-in-security') {
      const t = setTimeout(() => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 500);
      return () => clearTimeout(t);
    }
  }, []);
  // One group per LOCATION (several IPs can share a city) — drives BOTH the legend
  // labels and the map markers so counts always match between them.
  const groupedPoints = useMemo(() => {
    const groups = new Map();
    for (const p of mapPoints) {
      const key = p.location || p.ip;
      const g = groups.get(key) || { location: key, count: 0, suspicious: false, lat: null, lng: null, topCount: -1, ips: [], users: [], last_at: null, flags: [] };
      g.count += p.count;
      g.suspicious = g.suspicious || p.suspicious;
      g.ips.push(p);
      for (const f of p.flags || []) if (!g.flags.includes(f)) g.flags.push(f);
      if (p.last_at && (!g.last_at || new Date(p.last_at) > new Date(g.last_at))) g.last_at = p.last_at;
      for (const u of p.users || []) if (!g.users.includes(u)) g.users.push(u);
      if (p.lat != null && p.count > g.topCount) {
        g.lat = p.lat; g.lng = p.lng; g.topCount = p.count;
      }
      groups.set(key, g);
    }
    const arr = [...groups.values()].sort((a, b) => b.count - a.count);
    arr.forEach((g) => g.ips.sort((a, b) => b.count - a.count));
    return arr;
  }, [mapPoints]);

  return (
    <Card data-testid="device-sessions-card" ref={cardRef}>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Devices &amp; Sign-in Security
            </CardTitle>
            <CardDescription>
              {view === 'sessions'
                ? "Devices with an active session on your account. Sign out any you don't recognise."
                : view === 'map'
                  ? `Where your accounts signed in from over the last ${mapDays} days. Red markers had suspicious sign-ins.`
                  : 'Devices and IP networks remembered by suspicious-login detection. Remove one and the next sign-in from it is flagged and alerted again.'}
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={view === 'sessions' ? fetchDeviceSessions : view === 'map' ? fetchMap : fetchTrusted} disabled={loading} data-testid="sessions-refresh-btn" title="Refresh">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
            {view === 'sessions' && sessionsTotal > 1 && (
              <Button
                variant={confirmingKey === 'purge' ? 'destructive' : 'outline'}
                size="sm"
                onClick={handlePurgeStale}
                disabled={purging}
                title="Revoke every session idle for 30+ days (never this one)"
                data-testid="sessions-purge-stale"
              >
                {purging ? <Loader2 className="w-4 h-4 animate-spin" /> : (confirmingKey === 'purge' ? 'Confirm purge?' : <><Trash2 className="w-4 h-4 mr-2" />Purge stale (30d+)</>)}
              </Button>
            )}
            {view === 'sessions' && deviceSessions.filter(s => !s.current).length > 0 && (
              <Button variant="outline" size="sm" onClick={handleRevokeOtherSessions} data-testid="sessions-signout-others">
                <LogOut className="w-4 h-4 mr-2" />
                Sign out all others
              </Button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <div className="flex flex-wrap items-center gap-1 p-0.5 rounded-lg bg-muted/60 w-fit max-w-full" role="tablist">
            <Button
              variant="ghost"
              size="sm"
              className={`h-7 px-3 text-xs rounded-md ${view === 'sessions' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground'}`}
              onClick={() => setView('sessions')}
              aria-pressed={view === 'sessions'}
              data-testid="device-security-view-sessions"
            >
              Active sessions
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={`h-7 px-3 text-xs rounded-md ${view === 'trusted' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground'}`}
              onClick={() => setView('trusted')}
              aria-pressed={view === 'trusted'}
              data-testid="device-security-view-trusted"
            >
              Remembered devices &amp; networks
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={`h-7 px-3 text-xs rounded-md ${view === 'map' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground'}`}
              onClick={() => setView('map')}
              aria-pressed={view === 'map'}
              data-testid="device-security-view-map"
            >
              <MapIcon className="w-3.5 h-3.5 mr-1" />
              Sign-in map
            </Button>
          </div>
          {view === 'map' && (
          <>
          <div className="flex items-center gap-1 p-0.5 rounded-lg bg-muted/60 w-fit">
            {[7, 30, 90].map((d) => (
              <Button
                key={d}
                variant="ghost"
                size="sm"
                className={`h-7 px-2.5 text-xs rounded-md ${mapDays === d ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground'}`}
                onClick={() => setMapDays(d)}
                aria-pressed={mapDays === d}
                data-testid={`login-map-range-${d}`}
              >
                {d} days
              </Button>
            ))}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" disabled={mapPoints.length === 0} data-testid="login-map-export-menu">
                <Upload className="w-3 h-3" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={() => exportSigninMap({ points: mapPoints, days: mapDays, fmt: 'csv', api })} data-testid="login-map-export-csv">CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportSigninMap({ points: mapPoints, days: mapDays, fmt: 'html', api })} data-testid="login-map-export-html">HTML</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportSigninMap({ points: mapPoints, days: mapDays, fmt: 'pdf', api })} data-testid="login-map-export-pdf">PDF</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </>
          )}
          {view !== 'map' && (
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Search device, IP, location..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-7 pl-8 pr-7 text-xs"
              data-testid="device-security-search"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="device-security-clear-search">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          )}
        </div>
      </CardHeader>

      {view === 'sessions' && (
      <CardContent className="space-y-2" data-testid="device-sessions-list">
        {sessionsLoading && deviceSessions.length === 0 && (
          <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        )}
        {!sessionsLoading && deviceSessions.length === 0 && (
          <p className="text-sm text-muted-foreground py-2" data-testid="device-sessions-empty">
            {debouncedSearch ? 'No sessions match your search.' : 'No active sessions found.'}
          </p>
        )}
        {deviceSessions.map((s) => {
          const isPhone = /iPhone|iPad|Android/i.test(s.device_label);
          const DeviceIcon = isPhone ? Smartphone : Monitor;
          return (
            <div key={s.session_id} className="flex items-center gap-3 rounded-lg border bg-muted/20 px-3 py-2.5" data-testid={`session-row-${s.session_id}`}>
              <DeviceIcon className="w-5 h-5 text-muted-foreground shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium flex items-center gap-2">
                  <span className="truncate">{s.device_label}</span>
                  {s.current && (
                    <Badge variant="secondary" className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" data-testid={`session-current-badge-${s.session_id}`}>
                      This device
                    </Badge>
                  )}
                </div>
                {s.ip && (
                  <p className="text-xs text-muted-foreground" data-testid={`session-ip-${s.session_id}`}>
                    {s.ip}{s.location ? <span className="text-foreground/70"> · {s.location}</span> : ''}
                  </p>
                )}
                <p className="text-xs text-sky-700 dark:text-sky-400" data-testid={`session-last-active-${s.session_id}`}>
                  Last active {s.last_used_at ? formatDT(s.last_used_at) : 'unknown'}
                </p>
              </div>
              {!s.current && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950 shrink-0"
                  onClick={() => handleRevokeSession(s.session_id)}
                  data-testid={`session-signout-${s.session_id}`}
                >
                  <LogOut className="w-4 h-4 mr-1.5" />
                  Sign out
                </Button>
              )}
            </div>
          );
        })}
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-muted-foreground">{sessionsTotal} session{sessionsTotal === 1 ? '' : 's'}</span>
          <Pager page={page} pages={sessionPages} onPrev={() => setPage(p => p - 1)} onNext={() => setPage(p => p + 1)} prefix="sessions" />
        </div>
      </CardContent>
      )}

      {view === 'map' && (
      <CardContent className="space-y-3" data-testid="login-map-view">
        {mapLoading && mapPoints.length === 0 && (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        )}
        {!mapLoading && mapPoints.length === 0 && (
          <p className="text-sm text-muted-foreground py-2" data-testid="login-map-empty">No sign-ins recorded in the last {mapDays} days.</p>
        )}
        {mapPoints.length > 0 && (
          <>
            <div className="rounded-lg overflow-hidden border" style={{ height: 320 }} data-testid="login-map-container">
              <MapContainer
                ref={mapRef}
                center={[20, 0]}
                zoom={2}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={false}
              >
                <FitBounds points={mapPoints} />
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {groupedPoints.filter(g => g.lat != null).map((g) => (
                  <CircleMarker
                    key={g.location}
                    center={[g.lat, g.lng]}
                    radius={Math.min(6 + Math.sqrt(g.count) * 2, 18)}
                    pathOptions={{
                      color: g.suspicious ? '#dc2626' : '#2563eb',
                      fillColor: g.suspicious ? '#dc2626' : '#2563eb',
                      fillOpacity: 0.5,
                    }}
                  >
                    <Popup maxWidth={300}>
                      <div className="text-xs space-y-0.5">
                        <p className="font-semibold">{g.location || 'Unknown location'}</p>
                        <p>{g.count} sign-in{g.count === 1 ? '' : 's'} · last {g.last_at ? formatDT(g.last_at, { seconds: true }) : '—'}</p>
                        {g.last_at && <p className="text-muted-foreground font-mono text-[10px]" data-testid="login-map-popup-utc">{formatUTCStamp(g.last_at, { seconds: true })}</p>}
                        {g.ips.length > 0 && (
                          <div className="pt-1 space-y-0.5">
                            {g.ips.slice(0, 6).map((p) => (
                              <p key={p.ip} className={`font-mono text-[10px] ${p.suspicious ? 'text-red-600' : ''}`}>
                                {p.ip} · {p.count} sign-in{p.count === 1 ? '' : 's'}
                              </p>
                            ))}
                            {g.ips.length > 6 && <p className="text-[10px] text-muted-foreground">+{g.ips.length - 6} more IP addresses</p>}
                          </div>
                        )}
                        {g.users.length > 0 && <p className="text-muted-foreground">{g.users.slice(0, 5).join(', ')}</p>}
                        {g.flags?.includes('outside_home_region') && <p className="text-red-600 font-medium" data-testid="login-map-popup-geofence">Outside home region</p>}
                        {g.suspicious && <p className="text-red-600 font-medium">Included suspicious sign-ins</p>}
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="login-map-legend">
              {groupedPoints.map((g, i) => (
                <Badge
                  key={g.location}
                  variant="outline"
                  className={`text-[10px] font-normal ${g.suspicious ? 'border-red-400 text-red-600 dark:text-red-400' : ''} ${g.lat != null ? 'cursor-pointer hover:bg-muted' : 'opacity-70'}`}
                  onClick={() => { if (g.lat != null) mapRef.current?.flyTo([g.lat, g.lng], 10); }}
                  title={g.lat != null ? 'Click to zoom to this location' : 'No coordinates available for this location'}
                  data-testid={`login-map-legend-${i}`}
                >
                  {g.location} · {g.count}
                </Badge>
              ))}
              {groupedPoints.some(g => g.lat == null) && (
                <span className="text-[10px] text-muted-foreground self-center">Locations without coordinates are listed but not plotted.</span>
              )}
            </div>
          </>
        )}
      </CardContent>
      )}

      {view === 'trusted' && (
      <CardContent className="space-y-4" data-testid="trusted-devices-card">
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1.5">Remembered devices ({devicesTotal})</p>
          <div className="space-y-2" data-testid="trusted-devices-list">
            {trustedLoading && devices.length === 0 && (
              <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
            )}
            {!trustedLoading && devices.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="trusted-devices-empty">
                {devicesTotal > 0 ? 'No more devices on this page.' : (debouncedSearch ? 'No devices match your search.' : 'No remembered devices yet — they appear after your next sign-in.')}
              </p>
            )}
            {devices.map((d, idx) => {
              const isPhone = /iPhone|iPad|Android/i.test(d.device_label || '');
              const DeviceIcon = isPhone ? Smartphone : Monitor;
              const signalsOpen = expandedSignals.includes(d.device_key);
              const sigPairs = parseSignals(d.signals);
              return (
                <div key={d.device_key} className="rounded-lg border bg-muted/20 px-3 py-2.5" data-testid={`trusted-device-row-${idx}`}>
                  <div className="flex flex-wrap items-center gap-3">
                  <DeviceIcon className="w-5 h-5 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1 basis-40">
                    <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                      <span className="truncate">{d.device_label || 'Unknown device'}</span>
                      {d.active_sessions > 0 && (
                        <Badge variant="secondary" className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" data-testid={`trusted-device-active-badge-${idx}`}>
                          Active session
                        </Badge>
                      )}
                    </div>
                    {d.ip && (
                      <p className="text-xs text-muted-foreground" data-testid={`trusted-device-ip-${idx}`}>
                        {d.ip}{d.location ? <span className="text-foreground/70"> · {d.location}</span> : ''}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      First seen {d.first_seen ? formatDT(d.first_seen) : '—'} · Last {d.last_seen ? formatDT(d.last_seen) : '—'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 ml-auto">
                  <Button
                    variant="ghost" size="sm"
                    className={`h-7 px-2 shrink-0 ${signalsOpen ? 'bg-muted' : 'text-muted-foreground'}`}
                    onClick={() => setExpandedSignals((prev) => signalsOpen ? prev.filter(k => k !== d.device_key) : [...prev, d.device_key])}
                    title="View the hardware/browser signals captured for this device"
                    data-testid={`trusted-device-signals-toggle-${idx}`}
                  >
                    <Fingerprint className="w-3.5 h-3.5" />
                  </Button>
                  {d.active_sessions > 0 && (
                    <ConfirmBtn
                      kind="device" itemKey={d.device_key} signout
                      label="Forget this device AND sign out its active sessions"
                      icon={<span className="flex items-center gap-1 text-xs"><LogOut className="w-3.5 h-3.5" />Forget &amp; sign out</span>}
                      testId={`trusted-device-forget-signout-${idx}`}
                    />
                  )}
                  <ConfirmBtn
                    kind="device" itemKey={d.device_key}
                    label="Forget — next sign-in from it will be flagged as suspicious"
                    icon={<Trash2 className="w-3.5 h-3.5" />}
                    testId={`trusted-device-remove-${idx}`}
                  />
                  </div>
                  </div>
                  {signalsOpen && (
                    <div className="mt-2 pt-2 border-t flex flex-wrap gap-1.5" data-testid={`trusted-device-signals-${idx}`}>
                      {sigPairs.length > 0 ? sigPairs.map(([k, v]) => (
                        <Badge key={k} variant="outline" className="text-[10px] font-normal">{k}: {v || '—'}</Badge>
                      )) : (
                        <span className="text-[10px] text-muted-foreground" data-testid={`trusted-device-signals-empty-${idx}`}>
                          No hardware signals captured yet — they're recorded on this device's next sign-in.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1.5">Remembered IP networks ({networksTotal})</p>
          <div className="space-y-2" data-testid="trusted-networks-list">
            {!trustedLoading && networks.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="trusted-networks-empty">
                {networksTotal > 0 ? 'No more networks on this page.' : (debouncedSearch ? 'No networks match your search.' : 'No remembered networks yet — they appear after your next sign-in.')}
              </p>
            )}
            {networks.map((n, idx) => {
              const ips = n.ips || [];
              const expanded = expandedNets.includes(n.ip_key);
              return (
                <div key={n.ip_key} className="flex items-center gap-3 rounded-lg border bg-muted/20 px-3 py-2.5" data-testid={`trusted-network-row-${idx}`}>
                  <Globe className="w-5 h-5 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium font-mono">{n.ip_key}{n.ip_key.includes(':') ? '::/64' : '.x'}</p>
                      {(n.location || n.country_name) && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0" data-testid={`trusted-network-country-${idx}`}>{n.location || n.country_name}</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground font-mono" data-testid={`trusted-network-ips-${idx}`}>
                      {ips.length <= 3 || expanded ? ips.join(', ') : (
                        <>
                          {ips.slice(0, 3).join(', ')}{' '}
                          <button className="underline font-sans" onClick={() => setExpandedNets(prev => [...prev, n.ip_key])} data-testid={`trusted-network-expand-${idx}`}>
                            +{ips.length - 3} more
                          </button>
                        </>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      First seen {n.first_seen ? formatDT(n.first_seen) : '—'} · Last {n.last_seen ? formatDT(n.last_seen) : '—'}
                    </p>
                  </div>
                  <ConfirmBtn
                    kind="network" itemKey={n.ip_key}
                    label="Forget — next sign-in from it will be flagged as suspicious"
                    icon={<Trash2 className="w-3.5 h-3.5" />}
                    testId={`trusted-network-remove-${idx}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
        <Pager page={page} pages={trustedPages} onPrev={() => setPage(p => p - 1)} onNext={() => setPage(p => p + 1)} prefix="trusted" />
        <p className="text-[10px] text-muted-foreground flex items-center gap-1 flex-wrap" data-testid="geoip-status-line">
          <ShieldCheck className="w-3 h-3" />
          {geoipStatus?.release
            ? <>Country database: {geoipStatus.release} release{geoipStatus.last_checked ? <> · last checked {formatDT(geoipStatus.last_checked)}</> : null} · auto-updates monthly.</>
            : <>Country lookup runs locally on this server and auto-updates monthly.</>}
          {' '}<a href="https://db-ip.com" target="_blank" rel="noreferrer" className="underline">IP Geolocation by DB-IP</a>
        </p>
      </CardContent>
      )}
      <CardContent className="pt-0">
        <HomeRegionSection api={api} />
      </CardContent>
    </Card>
  );
};

export default DeviceSecurityCard;
